// components/consumer/ConsumerBottomNav.tsx
"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { Home, ReceiptText, Heart, Tag, UserRound } from "lucide-react"
import { accountHref } from "@/lib/site/consumerNav"

type Props = { homeHref: string; hasSession: boolean }

/**
 * Barra inferior de móvil (spec §4.7). Cinco destinos, como en las apps de
 * pedidos; en escritorio no aparece (md:hidden) porque manda la cabecera.
 * El activo sale de usePathname: "Pedidos" cubre /cuenta y /p/*, "Cuenta" cubre
 * /cuenta/perfil y /cuenta/entrar.
 */
export function ConsumerBottomNav({ homeHref, hasSession }: Props) {
  const t = useTranslations("public.consumerNav")
  const pathname = usePathname() ?? ""
  const items = [
    { key: "home", href: homeHref, label: t("home"), Icon: Home, active: pathname === homeHref || pathname === "/order" },
    { key: "orders", href: "/cuenta", label: t("orders"), Icon: ReceiptText, active: pathname === "/cuenta" || pathname.startsWith("/p/") },
    { key: "saved", href: "/cuenta/guardados", label: t("saved"), Icon: Heart, active: pathname.startsWith("/cuenta/guardados") },
    { key: "discounts", href: "/cuenta/descuentos", label: t("discounts"), Icon: Tag, active: pathname.startsWith("/cuenta/descuentos") },
    { key: "account", href: accountHref(hasSession), label: t("account"), Icon: UserRound, active: pathname.startsWith("/cuenta/perfil") || pathname.startsWith("/cuenta/entrar") },
  ]
  return (
    <nav
      aria-label={t("menuLabel")}
      className="fixed inset-x-0 bottom-0 z-30 border-t md:hidden"
      style={{ backgroundColor: "var(--redy-surface)", borderColor: "var(--redy-border)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-5">
        {items.map(({ key, href, label, Icon, active }) => (
          <li key={key}>
            <Link
              href={href}
              aria-current={active ? "page" : undefined}
              className="flex flex-col items-center gap-0.5 py-2 text-[11px] font-bold"
              style={{ color: active ? "var(--redy-red)" : "var(--redy-muted)" }}
            >
              <Icon size={22} strokeWidth={active ? 2.5 : 2} aria-hidden="true" />
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
